import React from 'react';
import { motion } from 'framer-motion';
import { FaAws, FaDocker, FaLinux, FaReact, FaPython, FaHtml5, FaCss3Alt, FaNodeJs, FaWindows, FaGoogle } from 'react-icons/fa';
import { SiTerraform, SiKubernetes, SiJenkins, SiGithubactions, SiMicrosoftazure, SiGrafana, SiCplusplus, SiGnubash, SiPrometheus, SiGooglecloud } from 'react-icons/si';

const About = () => {
  const calculateExperience = () => {
    const startDate = new Date(2024, 0);
    const currentDate = new Date();
    const months = (currentDate.getFullYear() - startDate.getFullYear()) * 12 + (currentDate.getMonth() - startDate.getMonth()) + 1;
    return (months / 12).toFixed(1);
  };

  const experience = calculateExperience();


  const skillCategories = [
    {
      category: 'Cloud Platforms',
      skills: [
        { name: 'AWS', icon: <FaAws /> },
        { name: 'GCP', icon: <SiGooglecloud /> },
        { name: 'Azure', icon: <SiMicrosoftazure /> }
      ]
    },
    {
      category: 'Programming Languages',
      skills: [
        { name: 'C++', icon: <SiCplusplus /> },
        { name: 'Python', icon: <FaPython /> },
        { name: 'Shell/Bash Scripting', icon: <SiGnubash /> }
      ]
    },
    {
      category: 'DevOps & Automation',
      skills: [
        { name: 'Terraform', icon: <SiTerraform />},
        { name: 'Jenkins', icon: <SiJenkins /> },
        { name: 'GitHub Actions', icon: <SiGithubactions /> },
        { name: 'Docker', icon: <FaDocker /> },
        { name: 'AWS CodePipeline, Commit, Build', icon: <FaAws /> },
        { name: 'Kubernetes', icon: <SiKubernetes /> }
      ]
    },
    {
      category: 'Server Administration',
      skills: [
        { name: 'Linux', icon: <FaLinux /> },
        { name: 'Windows (Active Directory, Certificates, Domains)', icon: <FaWindows /> }
      ]
    },
    {
      category: 'Monitoring and Logging',
      skills: [
        { name: 'CloudWatch', icon: <FaAws /> },
        { name: 'Prometheus', icon: <SiPrometheus /> },
        { name: 'Grafana', icon: <SiGrafana /> }
      ]
    },
    {
      category: 'Web Development',
      skills: [
        { name: 'HTML', icon: <FaHtml5 /> },
        { name: 'CSS', icon: <FaCss3Alt /> },
        { name: 'React.js', icon: <FaReact /> },
        { name: 'Node.js', icon: <FaNodeJs /> }
      ]
    },
    {
      category: 'Core Subjects',
      skills: [
        { name: 'Data Structures and Algorithms' },
        { name: 'Cloud Computing' },
        { name: 'OOPs' },
        { name: 'DBMS' },
        { name: 'OS' },
        { name: 'Computer Networks' },
        { name: 'Software Engineering' }
      ]
    },
    {
      category: 'AI & LLM Tools',
      skills: [
        { name: 'ChatGPT' },
        { name: 'Gemini' },
        { name: 'Perplexity AI' },
        { name: 'Amazon Q' },
        { name: 'Amazon Titan' },
        { name: 'Anthropic Claude 4' },
        { name: 'Agentic AI' },
        { name: 'MCP Server' }
      ]
    }
  ];

  const certifications = [
    { name: 'AWS Certified Solutions Architect – Associate (SAA-C03)', icon: <FaAws />, link: 'https://www.credly.com/badges/20bb2a9b-8a5e-4a69-885b-e44a3b19b4d8/public_url' },
    { name: 'AWS Certified Developer - Associate (DVA-C02)', icon: <FaAws />, link: 'https://www.credly.com/badges/58d41154-eb49-4ec8-98e7-4a3a55821b21/public_url' },
    { name: 'AWS Certified AI Practitioner (AIF-C01)', icon: <FaAws />, link: 'https://www.credly.com/badges/1f857aa0-0a95-4ddb-a0d9-e0e2f87c419b/public_url' },
    { name: 'GCP Professional Cloud Architect Certification', icon: <FaGoogle />, link: 'https://www.credly.com/badges/e928d099-ffc1-4993-839d-5ba52dc268c1/public_url' },
    { name: 'Terraform Certified Associate (HCTAO-003)', icon: <SiTerraform />, link: 'https://www.credly.com/badges/1976b2ca-8632-493a-ad74-6181cc39fe25/public_url' }
  ];

  return (
    <motion.div 
      className="min-h-screen pt-32 pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto px-5">
        <motion.section 
          className="mb-16"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="text-4xl font-bold text-center mb-12" style={{ color: 'var(--color-primary-text)' }}>About Me</h1>
          <motion.div 
            className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="xl:col-span-3 backdrop-blur-md p-6 rounded-xl border-2" style={{ backgroundColor: 'var(--color-card-bg)', borderColor: 'var(--color-card-border)' }}>
              <div className="text-lg text-justify leading-relaxed space-y-6" style={{ color: 'var(--color-primary-text)' }}>
                <p>
                  I’m a Cloud Engineer and AWS Community Builder with {experience} years of experience designing secure, scalable, and automated cloud systems. At Searce, I work across AWS and GCP, building production-ready infrastructure, cloud security architectures, and GenAI-powered applications.
                </p>
                <p>
                  My focus is on Infrastructure as Code, DevOps automation, performance optimization, and intelligent cloud solutions. I enjoy turning complex cloud challenges into reliable, cost-efficient systems and sharing what I learn with the global cloud community.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.section>

        <motion.section 
          className="mb-16"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: 'var(--color-primary-text)' }}>Technical Skills</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {skillCategories.map((category, index) => (
              <motion.div 
                key={category.category}
                className="backdrop-blur-md p-6 rounded-xl border-2 hover:shadow-xl transition-all"
                style={{ backgroundColor: 'var(--color-card-bg)', borderColor: 'var(--color-card-border)' }}
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--color-primary-text)' }}>{category.category}:</h3>
                {category.category === 'Core Subjects' || category.category === 'AI & LLM Tools' ? (
                  <p className="leading-relaxed" style={{ color: 'var(--color-accent)' }}>
                    {category.skills.map(skill => skill.name).join(', ')}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {category.skills.map((skill, skillIndex) => (
                      <div key={skillIndex} className="flex items-center gap-2">
                        {skill.icon && <span className="text-lg" style={{ color: 'var(--color-accent)' }}>{skill.icon}</span>}
                        <span style={{ color: 'var(--color-accent)' }}>{skill.name}</span>
                        {skillIndex < category.skills.length - 1 && <span style={{ color: 'var(--color-primary-text)' }}>,</span>}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section 
          className="mb-16"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: 'var(--color-primary-text)' }}>Certifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {certifications.map((cert, index) => (
              <motion.a 
                key={index}
                href={cert.link}
                target="_blank"
                rel="noopener noreferrer"
                className="backdrop-blur-md flex items-center gap-4 p-6 rounded-xl border-2 hover:shadow-xl transition-all cursor-pointer"
                style={{ backgroundColor: 'var(--color-card-bg)', borderColor: 'var(--color-card-border)' }}
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <span className="text-3xl" style={{ color: 'var(--color-accent)' }}>{cert.icon}</span>
                <span className="text-lg font-medium" style={{ color: 'var(--color-primary-text)' }}>{cert.name}</span>
              </motion.a>
            ))}
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default About; 
